import NiceModal from '@ebay/nice-modal-react';
import { css } from '@emotion/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Badge from 'components/field/Badge';
import Button from 'components/field/Button';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import SwiperFooter from 'components/global/SwiperFooter';
import Typography from 'components/global/Typography';

import useAccessControl from 'hooks/useAccessControl';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import SubscriptionAccountModal from 'pages/Settings/Payment/SubscriptionAccount/SubscriptionAccountModal';

import { Breakpoints } from 'utils/const.styles';

import { faCheck } from '@fortawesome/pro-solid-svg-icons';

import FeatureBreakdownModal from './FeatureBreakdownModal';
import * as S from './OTOModules.styled';
import UpgradePlanSuccessModal from './UpgradePlanSuccessModal';

const Packages = ({ planType, PlanTypes }) => {
	const { otoPackageList, modulesList } = useSelector((state) => state.otoPackageList);
	const user = useSelector((state) => state.user);
	const { t } = useTranslation();
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const [swiperRef, setSwiperRef] = useState(null);
	const { getLocaleDateWithTime } = useLocale();
	const isAuthorized = useAccessControl();

	const currentPlan = useMemo(
		() => otoPackageList.find((p) => p.planName === user.planName),
		[otoPackageList, user.planName]
	);

	const getPackageDetails = useCallback(
		(plan) => {
			// Basic status indicators
			const isCurrentPlan = plan.planName === user.planName;
			const isNextPackage = plan.planName === user.nextPackageName;
			const isEnterprisePlan = plan.planName === 'EnterprisePackage';
			const isActivePlan = isCurrentPlan && !user.accountSuspended;

			// Price calculations
			let displayPrice = plan.monthlyPrice;
			if (plan.pricePlaceholder) {
				displayPrice = t(plan.pricePlaceholder);
			} else if (planType === 'yearly') {
				displayPrice = plan.yearlyPrice === 0 ? 0 : plan.yearlyPrice / 12;
			}

			const originalPrice =
				planType === PlanTypes.yearly && plan.yearlyPriceWithoutDiscount
					? plan.yearlyPriceWithoutDiscount / 12
					: plan.monthlyPriceWithoutDiscount;

			// Button state
			const buttonDisabled = !isAuthorized('/oto-modules/account-owner-actions')
				? true
				: user.isDowngradeRequested
					? plan.planName !== 'EnterprisePackage'
					: isActivePlan || (user.nextPackageName !== user.planName && !isEnterprisePlan);

			// Determine if there's a pending change that requires a tooltip
			const hasPendingPackageChange = user.nextPackageName !== user.planName && !isEnterprisePlan;
			const isPendingChange = !isCurrentPlan && (user.isDowngradeRequested || hasPendingPackageChange);

			let buttonTooltip = null;
			if (isPendingChange) {
				buttonTooltip = t('requestedPackageButtonTooltip', {
					currentPlanExpiryDate: getLocaleDateWithTime(user.currentPlanExpiryDate)
				});
			}

			// Determine button label
			let buttonLabel = isEnterprisePlan ? plan.upgradeButtonLabel : 'upgradeNow';
			if (isActivePlan) {
				buttonLabel = 'selected';
			} else if (currentPlan && currentPlan.tier > plan.tier) {
				buttonLabel = 'downgradeNow';
			}

			// Button icon
			const buttonIcon = isActivePlan ? faCheck : null;

			return {
				isNextPackage,
				isEnterprisePlan,
				displayPrice,
				originalPrice,
				buttonDisabled,
				buttonTooltip,
				buttonLabel,
				buttonIcon
			};
		},
		[user, currentPlan, planType]
	);

	const handleClick = useCallback(
		async (plan) => {
			const result = await NiceModal.show(SubscriptionAccountModal, {
				currentPackage: otoPackageList.find((item) => item.planName === user.nextPackageName),
				selectedPackage: plan,
				selectedPlanType: planType
			});
			if (result.showOTOModuleMessage) {
				setTimeout(async () => {
					const navigateSection = await NiceModal.show(UpgradePlanSuccessModal, {
						title: t('activateModules.package.title', { plan: t(plan.planName) }),
						image: plan.packageIcon
					});
					if (navigateSection) {
						const selectedPackageRoleID = modulesList.find(
							(item) => item.tier === plan.tier
						)?.packageRoleID;

						if (!selectedPackageRoleID) return;
						const element = document.getElementById(selectedPackageRoleID);
						if (element) element.scrollIntoView({ behavior: 'smooth' });
					}
				}, 500);
			}
		},
		[planType]
	);

	return (
		<S.PackageList>
			<Swiper
				slidesPerView={isMobile ? 1 : 4}
				modules={[Autoplay, Pagination, Navigation]}
				autoplay={{
					delay: 5000,
					disableOnInteraction: false
				}}
				onSwiper={setSwiperRef}>
				{otoPackageList.map((plan) => {
					const {
						isNextPackage,
						isEnterprisePlan,
						displayPrice,
						originalPrice,
						buttonDisabled,
						buttonTooltip,
						buttonLabel,
						buttonIcon
					} = getPackageDetails(plan);
					return (
						<SwiperSlide key={plan.planName}>
							<S.PackageItem>
								<S.MostPopular show={plan.popularBadge}>
									<Typography variant="caption" fontWeight="500" color="var(--white)">
										mostPopular
									</Typography>
								</S.MostPopular>

								<S.PackageItemContent>
									<S.PackageItemImage>
										<Image
											url={plan.packageIcon}
											alt={plan.planName}
											width="7.2rem"
											height="7.2rem"
										/>
									</S.PackageItemImage>
									<S.PlanName>
										<Typography variant="p1" fontWeight="500">
											{plan.planName}
										</Typography>
										{isNextPackage && (
											<Badge variant="pill" size="small" color="blue" text="current" />
										)}
									</S.PlanName>
									<S.PriceContainer>
										<Typography
											component="s"
											color="var(--red)"
											withCurrency
											currency={plan.currency}>
											{originalPrice}
										</Typography>
										<S.Price>
											<Typography
												variant="p1"
												fontWeight="bold"
												className="priceResponsiveFontSize"
												withCurrency={!isEnterprisePlan}
												currency={plan.currency}>
												{displayPrice}
											</Typography>
											{!isEnterprisePlan && (
												<>
													<Typography variant="p2" fontWeight="bold">
														/
													</Typography>
													<Typography variant="p2" fontWeight="bold">
														{planType === PlanTypes.yearly
															? 'yearlyPackagePrice'
															: 'monthly'}
													</Typography>
												</>
											)}
										</S.Price>
									</S.PriceContainer>
									<Button
										id={`${plan.planName}-${buttonLabel}`}
										label={buttonLabel}
										icon={buttonIcon}
										disabled={buttonDisabled}
										disabledTooltip={buttonTooltip}
										onClick={() => handleClick(plan)}
										fullWidth
									/>
									<S.FeaturesContainer>
										<Typography variant="p3" fontWeight="500">
											planHighlights
										</Typography>
										<S.FeatureItem>
											{plan.properties.map((feature) => (
												<S.FeatureItemText key={feature}>
													<Icon color="var(--green)" icon={faCheck} />
													<Typography variant="p3" noWrap>
														{feature}
													</Typography>
												</S.FeatureItemText>
											))}
										</S.FeatureItem>
										<Button
											className="mt-2"
											label="learnMore"
											variant="blackOutline"
											onClick={() => {
												NiceModal.show(FeatureBreakdownModal, { planType });
											}}
											css={css`
												text-decoration: underline;
												border: none !important;
											`}
										/>
									</S.FeaturesContainer>
								</S.PackageItemContent>
							</S.PackageItem>
						</SwiperSlide>
					);
				})}
				{otoPackageList.length > (isMobile ? 1 : 4) && swiperRef && (
					<SwiperFooter cssPosition="relative" swiperRef={swiperRef} />
				)}
			</Swiper>
		</S.PackageList>
	);
};

export default Packages;
